"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { Layers, MapPin, Scale } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/resources/salary-insights";
const APP = "/sign-up";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "salary-ranges",
    name: "Salary Ranges",
    title: "See What the Role Really Pays",
    description:
      "Look up pay for any job title by experience level and city, shown as low, median and high, so you know where an offer sits.",
    imageSrcUrl: `${IMG}/hero-salary-ranges.png`,
    primaryActionText: "Explore Salaries",
    primaryActionLink: APP,
    secondaryActionText: "Career Guides",
    secondaryActionLink: "/resources/career-guides",
  },
  {
    id: "offer-check",
    name: "Offer Check",
    title: "Find Out if Your Offer Is Fair",
    description:
      "Enter an offer and see which percentile it falls in for that role and city, and how much room you have to negotiate.",
    imageSrcUrl: `${IMG}/hero-offer-check.png`,
    primaryActionText: "Check My Offer",
    primaryActionLink: APP,
    secondaryActionText: "Career Guides",
    secondaryActionLink: "/resources/career-guides",
  },
  {
    id: "negotiation",
    name: "Negotiation Scripts",
    title: "Negotiate With the Numbers Behind You",
    description:
      "A step-by-step playbook from the first salary question to the final email, with your target and walk-away numbers in view.",
    imageSrcUrl: `${IMG}/hero-scripts.png`,
    primaryActionText: "Explore Salaries",
    primaryActionLink: APP,
    secondaryActionText: "Score My Resume",
    secondaryActionLink: "/features/resume-jobfit-scorer",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "pay-by-level-city",
    title: "Pay by Level and City",
    subtitle: "COMPARE LIKE FOR LIKE",
    description:
      "Compare pay for the same role across experience levels and cities. See how much a promotion or a move to another city changes your range.",
    benefits: [
      "Junior to staff levels",
      "City-by-city comparison",
      "Low, median and high",
      "Remote and on-site ranges",
    ],
    primaryCTA: "Explore Salaries",
    primaryLink: APP,
    secondaryCTA: "Career Guides",
    secondaryLink: "/resources/career-guides",
    imageSrcUrl: `${IMG}/pay-by-level.png`,
    icon: <MapPin className="w-4 h-4" />,
    stats: "By role, level and city",
  },
  {
    id: "total-compensation",
    title: "Total Compensation, Explained",
    subtitle: "LOOK PAST THE BASE",
    description:
      "Base salary is only part of the offer. See how bonus, equity and a joining bonus add up each year, and how equity vests over four years.",
    benefits: [
      "Base, bonus and equity split",
      "Year-by-year totals",
      "Vesting schedule shown",
      "Four-year value in one number",
    ],
    primaryCTA: "See Total Pay",
    primaryLink: APP,
    secondaryCTA: "Negotiation Guides",
    secondaryLink: "/resources/career-guides",
    imageSrcUrl: `${IMG}/total-comp.png`,
    icon: <Layers className="w-4 h-4" />,
    stats: "Four-year view",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "offer-comparison",
    title: "Offer Comparison",
    subtitle: "TWO OFFERS, ONE CLEAR ANSWER",
    description:
      "Have more than one offer? Put them side by side and compare base, bonus, equity and joining bonus over four years, so you choose on total value.",
    benefits: [
      "Offers side by side",
      "Four-year totals",
      "Equity and bonus included",
      "Highest value highlighted",
    ],
    primaryCTA: "Compare My Offers",
    primaryLink: APP,
    secondaryCTA: "See Plans",
    secondaryLink: "/pricing",
    imageSrcUrl: `${IMG}/offer-comparison.png`,
    icon: <Scale className="w-4 h-4" />,
    stats: "Compare on total value",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "The offer check put my offer at the 38th percentile. I countered with the median and they met me halfway the same week.",
    name: "Ananya R.",
    role: "Product Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "I almost took the offer with the higher base. The four-year view showed the other one was worth far more once equity was counted.",
    name: "Marcus T.",
    role: "DevOps Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "Seeing the range for my level in Bengaluru gave me the confidence to stop quoting my current CTC.",
    name: "Jalen Kyle",
    role: "Project Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=600&h=800&fit=crop&q=80",
  },
];

export default function SalaryInsightsPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Know Your Worth",
            titleHighlight: "Before You Negotiate.",
            subtitle:
              "Salary ranges by role, level and city, a clear breakdown of base, bonus and equity, and the scripts to ask for what the role pays.",
            ctaText: "Explore Salaries",
            ctaLink: APP,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Pay, Explained.",
            sectionSubtitle: "Understand the numbers before you name one.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Better Offers, Clearer Choices",
            subtitle: "From job seekers who negotiated with the data",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Salary Insights",
            subtitle: "Where the numbers come from and how to use them.",
            faqs,
            ctaTitle: "Find out what your next role should pay.",
            ctaText: "Explore Salaries",
            ctaLink: APP,
          },
        },
      ]}
    />
  );
}
